import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { INDIAN_STATES } from '../../../shared/constants/indian-states.constants';
import {
  BUDGET_RANGE_OPTIONS,
  COUNTRY_OPTIONS,
  COURSE_OPTIONS,
  ENGLISH_TEST_OPTIONS,
  INTAKE_OPTIONS,
  QUALIFICATION_OPTIONS,
  YES_NO_OPTIONS,
} from '../../../shared/constants/lead-form-options.constants';
import { LeadsService } from '../../../shared/services/leads.service';
import { CountriesService } from '../../../shared/services/countries.service';
import { LeadAttribution, LeadAttributionService } from '../../services/lead-attribution.service';

export type LeadCaptureFormData = {
  fullName: string;
  phoneNumber: string;
  whatsappNumber: string;
  email: string;
  city: string;
  state: string;
  country: string;
  qualification: string;
  percentageOrCgpa: string;
  passingYear: string;
  institution: string;
  preferredDestination: string;
  preferredCourse: string;
  intake: string;
  budgetRange: string;
  passportStatus: string;
  englishTestStatus: string;
  loanRequired: string;
};

const EMPTY_FORM: LeadCaptureFormData = {
  fullName: '',
  phoneNumber: '',
  whatsappNumber: '',
  email: '',
  city: '',
  state: '',
  country: 'India',
  qualification: '',
  percentageOrCgpa: '',
  passingYear: '',
  institution: '',
  preferredDestination: '',
  preferredCourse: '',
  intake: '',
  budgetRange: '',
  passportStatus: '',
  englishTestStatus: '',
  loanRequired: '',
};

const STEPS = [
  { id: 1, label: 'Basic Info' },
  { id: 2, label: 'Academic' },
  { id: 3, label: 'Preferences' },
] as const;

@Component({
  selector: 'app-lead-capture-form',
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './lead-capture-form.component.html',
})
export class LeadCaptureFormComponent implements OnInit {
  private readonly leads = inject(LeadsService);
  private readonly countries = inject(CountriesService);
  private readonly attribution = inject(LeadAttributionService);

  readonly compact = input(false);
  readonly sourcePage = input('/lead-form');
  readonly submitted = output<void>();

  readonly steps = STEPS;
  readonly currentStep = signal(1);
  readonly submitting = signal(false);
  readonly submitOk = signal(false);
  readonly submitOkMessage = signal('');
  readonly submitError = signal<string | null>(null);
  readonly fieldErrors = signal<Partial<Record<keyof LeadCaptureFormData, string>>>({});

  readonly form: LeadCaptureFormData = { ...EMPTY_FORM };

  readonly stateOptions = [...INDIAN_STATES];
  readonly countryOptions = [...COUNTRY_OPTIONS];
  readonly qualificationOptions = [...QUALIFICATION_OPTIONS];
  readonly courseOptions = [...COURSE_OPTIONS];
  readonly intakeOptions = [...INTAKE_OPTIONS];
  readonly budgetOptions = [...BUDGET_RANGE_OPTIONS];
  readonly englishTestOptions = [...ENGLISH_TEST_OPTIONS];
  readonly yesNoOptions = [...YES_NO_OPTIONS];
  readonly destinationOptions = signal<string[]>([]);

  private leadAttribution: LeadAttribution | null = null;

  ngOnInit(): void {
    this.leadAttribution = this.attribution.captureFromRoute();
    this.countries.listPublicCountries().subscribe({
      next: (list) => {
        const fromApi = list.map((c) => c.name?.trim()).filter((v): v is string => Boolean(v));
        const fallback = ['United Kingdom', 'Canada', 'Germany', 'Australia', 'United States', 'Ireland'];
        this.destinationOptions.set(
          Array.from(new Set([...fromApi, ...fallback])).sort((a, b) => a.localeCompare(b)),
        );
      },
      error: () => {
        this.destinationOptions.set([
          'United Kingdom',
          'Canada',
          'Germany',
          'Australia',
          'United States',
          'Ireland',
          'France',
          'New Zealand',
        ]);
      },
    });
  }

  get progressPercent(): number {
    return Math.round((this.currentStep() / STEPS.length) * 100);
  }

  goToStep(step: number): void {
    if (step < this.currentStep() || this.validateStep(this.currentStep())) {
      this.currentStep.set(step);
      this.submitError.set(null);
    }
  }

  nextStep(): void {
    if (!this.validateStep(this.currentStep())) return;
    if (this.currentStep() < STEPS.length) {
      this.currentStep.update((s) => s + 1);
      this.submitError.set(null);
    }
  }

  prevStep(): void {
    if (this.currentStep() > 1) {
      this.currentStep.update((s) => s - 1);
      this.submitError.set(null);
    }
  }

  submit(): void {
    if (!this.validateStep(1) || !this.validateStep(2) || !this.validateStep(3)) {
      const errors = this.fieldErrors();
      if (errors.fullName || errors.email || errors.phoneNumber) this.currentStep.set(1);
      else if (errors.qualification) this.currentStep.set(2);
      else this.currentStep.set(3);
      return;
    }

    const attr = this.leadAttribution ?? this.attribution.captureFromRoute();
    this.submitting.set(true);
    this.submitError.set(null);
    this.submitOk.set(false);

    this.leads
      .submitPublic({
        fullName: this.form.fullName.trim(),
        email: this.form.email.trim(),
        phoneNumber: this.form.phoneNumber.trim(),
        whatsappNumber: this.form.whatsappNumber.trim() || undefined,
        city: this.form.city.trim() || undefined,
        state: this.form.state.trim() || undefined,
        country: this.form.country.trim() || undefined,
        qualification: this.form.qualification.trim() || undefined,
        percentageOrCgpa: this.form.percentageOrCgpa.trim() || undefined,
        passingYear: this.form.passingYear.trim() || undefined,
        institution: this.form.institution.trim() || undefined,
        preferredDestination: this.form.preferredDestination.trim(),
        preferredCourse: this.form.preferredCourse.trim() || undefined,
        intake: this.form.intake.trim() || undefined,
        budgetRange: this.form.budgetRange.trim() || undefined,
        passportStatus: this.form.passportStatus.trim() || undefined,
        englishTestStatus: this.form.englishTestStatus.trim() || undefined,
        loanRequired: this.form.loanRequired.trim() || undefined,
        leadSource: attr.leadSource,
        sourcePage: this.buildSourcePage(attr),
        campaignName: attr.campaignName,
        adSetName: attr.adSetName,
        utmSource: attr.utmSource,
        utmMedium: attr.utmMedium,
        utmCampaign: attr.utmCampaign,
        utmTerm: attr.utmTerm,
        utmContent: attr.utmContent,
      })
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.submitOk.set(true);
          this.submitOkMessage.set(
            'Thank you! Your application has been received. A senior counselor will contact you within 24 hours.',
          );
          Object.assign(this.form, EMPTY_FORM);
          this.form.country = 'India';
          this.currentStep.set(1);
          this.submitted.emit();
        },
        error: (err) => {
          this.submitting.set(false);
          const status = err?.status;
          const message = err?.error?.message as string | undefined;
          if (status === 409 && message) {
            this.submitOk.set(true);
            this.submitOkMessage.set(message);
            Object.assign(this.form, EMPTY_FORM);
            this.form.country = 'India';
            this.currentStep.set(1);
            this.submitted.emit();
            return;
          }
          this.submitError.set(message ?? 'Something went wrong. Please try again.');
        },
      });
  }

  private buildSourcePage(attr: LeadAttribution): string {
    const base = this.sourcePage();
    const parts = new URLSearchParams();
    if (attr.utmSource) parts.set('utm_source', attr.utmSource);
    if (attr.utmMedium) parts.set('utm_medium', attr.utmMedium);
    if (attr.utmCampaign) parts.set('utm_campaign', attr.utmCampaign);
    if (attr.campaignName) parts.set('campaign', attr.campaignName);
    if (attr.adSetName) parts.set('adset', attr.adSetName);
    parts.set('captured_at', attr.capturedAt);
    const qs = parts.toString();
    return qs ? `${base}?${qs}` : base;
  }

  private validateStep(step: number): boolean {
    const errors: Partial<Record<keyof LeadCaptureFormData, string>> = {};

    if (step === 1) {
      if (!this.form.fullName.trim()) errors.fullName = 'Full name is required';
      if (!this.form.phoneNumber.trim()) errors.phoneNumber = 'Mobile number is required';
      else if (!/^[\d\s+\-()]{8,18}$/.test(this.form.phoneNumber.trim())) {
        errors.phoneNumber = 'Enter a valid mobile number';
      }
      if (!this.form.email.trim()) errors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())) {
        errors.email = 'Enter a valid email address';
      }
      if (!this.form.city.trim()) errors.city = 'City is required';
    }

    if (step === 2) {
      if (!this.form.qualification.trim()) errors.qualification = 'Current qualification is required';
    }

    if (step === 3) {
      if (!this.form.preferredDestination.trim()) {
        errors.preferredDestination = 'Preferred country is required';
      }
      if (!this.form.preferredCourse.trim()) errors.preferredCourse = 'Preferred course is required';
      if (!this.form.intake.trim()) errors.intake = 'Intake is required';
    }

    this.fieldErrors.set(errors);
    return Object.keys(errors).length === 0;
  }
}
